import { useState, useEffect, useCallback, useRef } from 'react'
import type { GalleryImage, LoadState, TargetResolution } from '../types'
import { galleryConfig } from '../config/galleryConfig'

interface UseProgressiveImageResult {
  displayedSrc: string | null   // Currently shown image (never changes during load)
  loadingSrc: string | null     // Image being loaded in background
  loadState: LoadState
  isTransitioning: boolean      // True when new image just loaded and ready to swap
  handleError: () => void
  onTransitionEnd: () => void   // Call when CSS transition completes
}

/**
 * Hook for progressive image loading with automatic URL refresh on 403 errors
 *
 * Loading sequence:
 * 1. Show placeholder (base64 blur) immediately
 * 2. Load thumbnail (fast, cached by CloudFront)
 * 3. Load medium/full based on zoom level (if user has access)
 */
export function useProgressiveImage(
  image: GalleryImage,
  isVisible: boolean,
  targetResolution: TargetResolution,
  refreshUrl: (id: string, resolution: 'medium' | 'full' | 'original') => Promise<string>
): UseProgressiveImageResult {
  // Track displayed (stable) and loading (background) images separately
  const [loadState, setLoadState] = useState<LoadState>('placeholder')
  const [displayedSrc, setDisplayedSrc] = useState<string | null>(() => image.urls.placeholder)
  const [loadingSrc, setLoadingSrc] = useState<string | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const mountedRef = useRef(true)
  const loadingRef = useRef<string | null>(null)

  // Reset on unmount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Handle visibility changes - DON'T reset when becoming visible again
  // Only reset when going invisible (memory management)
  useEffect(() => {
    if (!isVisible) {
      // Going invisible - reset to save memory
      setLoadState('placeholder')
      setDisplayedSrc(image.urls.placeholder)
      setLoadingSrc(null)
      setIsTransitioning(false)
      setRetryCount(0)
      loadingRef.current = null
    }
  }, [isVisible, image.id, image.urls.placeholder])

  // Ensure we always have at least a placeholder showing
  useEffect(() => {
    if (isVisible && !displayedSrc && !loadingSrc) {
      setDisplayedSrc(image.urls.placeholder || null)
    }
  }, [isVisible, displayedSrc, loadingSrc, image.urls.placeholder])

  // Recovery: If visible for 2 seconds with no image, force reset and retry
  useEffect(() => {
    if (!isVisible) return
    if (displayedSrc || loadingSrc) return // Already have an image

    const recovery = setTimeout(() => {
      console.log('[Gallery] Recovery: forcing reload for stuck image', image.id)
      setLoadState('placeholder')
      setDisplayedSrc(image.urls.placeholder || null)
      setRetryCount(0)
      loadingRef.current = null
    }, 2000)

    return () => clearTimeout(recovery)
  }, [isVisible, displayedSrc, loadingSrc, image.id, image.urls.placeholder])

  // Progressive loading sequence - loads in background, then triggers transition
  useEffect(() => {
    if (!isVisible || !mountedRef.current) return
    // Don't start new loads while transitioning, but don't block forever
    if (isTransitioning) return

    // Clear any stuck loading state when becoming visible
    if (loadingRef.current && !loadingSrc) {
      loadingRef.current = null
    }

    const loadNext = async () => {
      // Step 1: Load thumbnail
      if (loadState === 'placeholder' && image.urls.thumbnail) {
        if (loadingRef.current === 'thumbnail') return
        loadingRef.current = 'thumbnail'

        try {
          await preloadImage(image.urls.thumbnail, 8000)
          if (mountedRef.current && isVisible) {
            setLoadingSrc(image.urls.thumbnail)
            setIsTransitioning(true)
            setLoadState('thumbnail')
          }
        } catch {
          // Failed - clear loading state so it can retry
          console.warn('[Gallery] Thumbnail load failed, will retry')
        }
        loadingRef.current = null
        return
      }

      // Step 2: Load medium resolution (from thumbnail)
      // Always load medium first as intermediate step
      if (loadState === 'thumbnail' && image.hasAccess && targetResolution !== 'thumbnail') {
        let mediumUrl = image.urls.medium

        // OPTIMIZATION: If URL is null (thumbnailOnly mode), fetch on-demand
        if (!mediumUrl) {
          if (loadingRef.current === 'medium-fetch') return
          loadingRef.current = 'medium-fetch'
          try {
            mediumUrl = await refreshUrl(image.id, 'medium')
          } catch {
            loadingRef.current = null
            return // Failed to get URL, stay on thumbnail
          }
        }

        if (loadingRef.current === 'medium') return
        loadingRef.current = 'medium'

        try {
          await preloadImage(mediumUrl, 15000)
          if (mountedRef.current && isVisible) {
            setLoadingSrc(mediumUrl)
            setIsTransitioning(true)
            setLoadState('medium')
          }
        } catch {
          // Failed - stay on thumbnail
        }
        loadingRef.current = null
        return
      }

      // Step 3: Upgrade to full (only when zoomed in to 100%+)
      // Skip original for gallery - it's reserved for future modal view
      if (
        image.hasAccess &&
        targetResolution === 'full' &&
        loadState === 'medium'
      ) {
        let fullUrl = image.urls.full

        // OPTIMIZATION: If URL is null (thumbnailOnly mode), fetch on-demand
        if (!fullUrl) {
          if (loadingRef.current === 'full-fetch') return
          loadingRef.current = 'full-fetch'
          try {
            fullUrl = await refreshUrl(image.id, 'full')
          } catch {
            loadingRef.current = null
            return // Failed to get URL, stay on medium
          }
        }

        if (loadingRef.current === 'full') return
        loadingRef.current = 'full'

        try {
          await preloadImage(fullUrl, 20000)
          if (mountedRef.current && isVisible) {
            setLoadingSrc(fullUrl)
            setIsTransitioning(true)
            setLoadState('full')
          }
        } catch {
          // Failed - stay on medium
        }
        loadingRef.current = null
      }
    }

    // Load immediately (was 50ms delay - removed for faster gallery load)
    const timer = setTimeout(loadNext, 0)
    return () => clearTimeout(timer)
  }, [isVisible, loadState, image.hasAccess, image.urls, targetResolution, isTransitioning, loadingSrc])

  // Called when CSS transition completes - swap displayed src
  const onTransitionEnd = useCallback(() => {
    if (loadingSrc) {
      setDisplayedSrc(loadingSrc)
      setLoadingSrc(null)
      setIsTransitioning(false)
    }
  }, [loadingSrc])

  // Handle image load errors
  const handleError = useCallback(async () => {
    if (retryCount >= galleryConfig.loading.maxRetries) {
      console.warn(`[Gallery] Max retries reached for image ${image.id}`)
      if (image.urls.thumbnail && displayedSrc !== image.urls.thumbnail) {
        setDisplayedSrc(image.urls.thumbnail)
        setLoadingSrc(null)
        setIsTransitioning(false)
        setLoadState('thumbnail')
      }
      return
    }

    if (loadState === 'medium' || loadState === 'full' || loadState === 'original') {
      try {
        const freshUrl = await refreshUrl(image.id, loadState as 'medium' | 'full' | 'original')
        if (mountedRef.current) {
          setLoadingSrc(freshUrl)
          setIsTransitioning(true)
          setRetryCount(prev => prev + 1)
        }
      } catch (err) {
        console.error('[Gallery] Failed to refresh URL:', err)
        if (image.urls.thumbnail) {
          setDisplayedSrc(image.urls.thumbnail)
          setLoadingSrc(null)
          setIsTransitioning(false)
          setLoadState('thumbnail')
        }
      }
    }
  }, [retryCount, loadState, image.id, image.urls.thumbnail, displayedSrc, refreshUrl])

  return {
    displayedSrc,
    loadingSrc,
    loadState,
    isTransitioning,
    handleError,
    onTransitionEnd
  }
}

/**
 * Helper to preload an image with timeout
 * Prevents images from getting stuck if load hangs
 */
function preloadImage(src: string, timeoutMs: number = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const timeout = setTimeout(() => {
      img.onload = null
      img.onerror = null
      reject(new Error('Image load timeout'))
    }, timeoutMs)

    img.onload = () => {
      clearTimeout(timeout)
      resolve()
    }
    img.onerror = () => {
      clearTimeout(timeout)
      reject(new Error('Failed to load image'))
    }
    img.src = src
  })
}

/**
 * Determine target resolution based on zoom level
 */
export function getTargetResolution(scale: number): TargetResolution {
  const { zoomThresholds } = galleryConfig.images

  if (scale < zoomThresholds.thumbnail) {
    return 'thumbnail'
  }
  if (scale < zoomThresholds.medium) {
    return 'medium'
  }
  if (scale < zoomThresholds.full) {
    return 'full'
  }
  return 'original'
}
